import { apiGet, apiPost } from './utils';

export interface FileRecord {
  key: string;
  uuid: string;
  user?: string;
  identity?: string; // Kept for backwards compatibility
  filename: string;
  disposition: 'inline' | 'attachment';
  content_type: string;
  thumb_ticket?: string;
  thumb_width?: number;
  thumb_height?: number;
  date_created: string;
  date_updated: string;
}

export interface FileUploadTicket {
  url: string;
  ticket: string;
}

export const fileApi = {
  /**
   * Get a pre-signed upload URL (Step 1 of upload process)
   * Uses user authentication - no identity needed
   */
  async getUploadTicket(): Promise<FileUploadTicket> {
    return apiGet<FileUploadTicket>('/file/get-upload-ticket', {});
  },

  /**
   * Create a file record after uploading to GCS (Step 2 of upload process)
   * Uses user authentication - no identity needed
   */
  async createWithTicket(
    ticket: string,
    filename: string,
    contentType: string,
    disposition: 'inline' | 'attachment' = 'attachment'
  ): Promise<FileRecord> {
    return apiPost<FileRecord>('/file/create-with-ticket', {
      ticket,
      filename,
      content_type: contentType,
      disposition,
    });
  },

  /**
   * Get file by UUID
   * Uses user authentication - no identity needed
   */
  async getByUuid(uuid: string): Promise<FileRecord> {
    return apiGet<FileRecord>('/file/by-uuid', {
      uuid,
    });
  },

  /**
   * Get a signed download URL for a file
   */
  async getDownloadUrl(
    associationUuid: string,
    disposition: 'inline' | 'attachment' = 'attachment'
  ): Promise<{ url: string }> {
    return apiGet<{ url: string }>('/file/get-download-url', {
      association_uuid: associationUuid,
      disposition,
    });
  },

  /**
   * Get a signed thumbnail URL for a file
   */
  async getThumbnailUrl(
    identityKey: string,
    associationUuid: string,
    disposition: 'inline' | 'attachment' = 'inline'
  ): Promise<{ url: string }> {
    return apiGet<{ url: string }>('/file/get-thumbnail-url', {
      identity: identityKey,
      association_uuid: associationUuid,
      disposition,
    });
  },

  /**
   * Upload a file (combines getUploadTicket + PUT + createWithTicket)
   * Uses user authentication - no identity needed
   */
  async uploadFile(
    file: File,
    disposition: 'inline' | 'attachment' = 'attachment'
  ): Promise<FileRecord> {
    // Step 1: Get upload ticket
    const ticket = await fileApi.getUploadTicket();

    // Step 2: Upload to GCS using the signed URL
    const uploadResponse = await fetch(ticket.url, {
      method: 'PUT',
      body: file,
      headers: {
        'Content-Type': file.type || 'application/octet-stream',
      },
    });

    if (!uploadResponse.ok) {
      throw new Error(`Upload failed with status ${uploadResponse.status}`);
    }

    // Step 3: Create file record
    return fileApi.createWithTicket(
      ticket.ticket,
      file.name,
      file.type || 'application/octet-stream',
      disposition
    );
  },
};
